import React from 'react';
import { bountyRecordNote } from '../../utils/bountyEvidence';

// Security & Resilience · Evidence — SR-355 + SR-366: legacy v1 is shown as the CURRENT score-bearing
// methodology (LEGACY · PARTIAL), never as a 'diagnostic'. Evidence rows carry their evidence state;
// no pass / fail check marks. The legacy v1 number is shown only with its qualifiers, and the W2 (v2)
// evaluation is labelled shadow until it is binding.

interface V2Component { id: string; label: string; state: string; score?: number; basis: string; nominal_weight: number; load_bearing?: boolean }
interface V2Eval { mode: string; score?: number; coverage: number; withheld_reason?: string; components: V2Component[] }

const STATE_TONE: Record<string, string> = {
  VERIFIED: 'border-[color:rgb(var(--c-safe-rgb)/0.6)] text-[color:var(--c-hex-a3b07a)]',
  BINDING: 'border-[color:rgb(var(--c-safe-rgb)/0.6)] text-[color:var(--c-hex-a3b07a)]',
  CURATED: 'border-[color:rgb(var(--c-ink-rgb)/0.18)] text-[color:var(--c-text-strong)]',
  'CURATED ESTIMATE': 'border-[color:rgb(var(--c-ink-rgb)/0.18)] text-[color:var(--c-text-strong)]',
  'NOT VERIFIED': 'border-[color:rgb(var(--c-gold-dim-rgb)/0.6)] text-[color:var(--c-amber)]',
  WITHHELD: 'border-[color:rgb(var(--c-gold-dim-rgb)/0.6)] text-[color:var(--c-amber)]',
  'NOT ASSESSED': 'border-[color:rgb(var(--c-ink-rgb)/0.12)] text-[color:var(--c-text-soft)]',
  'NOT SCORED': 'border-[color:rgb(var(--c-ink-rgb)/0.12)] text-[color:var(--c-text-soft)]',
  PENDING: 'border-[color:rgb(var(--c-ink-rgb)/0.12)] text-[color:var(--c-text-soft)]',
};

function Badge({ state }: { state: string }) {
  return <span className={`rounded border px-1.5 py-0.5 font-mono text-[10.5px] tracking-wide ${STATE_TONE[state] ?? STATE_TONE.CURATED}`}>{state}</span>;
}

// disclosureText renders the curated disclosure evidence as a short, plain label.
function disclosureText(disc: V2Component | undefined, metrics: any): string {
  if (!disc) return metrics?.bug_bounty_active ? 'Active programme (curated status)' : 'Not confirmed';
  const m = disc.basis.match(/maximum payout \$([\d,.]+)/);
  if (disc.state === 'BINDING' && m) return `Active bug-bounty programme · max payout $${Number(m[1].replace(/,/g, '')).toLocaleString('en-US')}`;
  if (disc.state === 'BINDING') return /no disclosure program/.test(disc.basis) ? 'No disclosure programme found' : 'Active bug-bounty programme · max payout unverified';
  if (/policy without a monetary bounty/.test(disc.basis)) return 'Responsible-disclosure policy · no monetary bounty';
  return 'No curated disclosure record';
}

function openSecurityDossier() {
  try {
    const u = new URL(window.location.href);
    u.searchParams.set('dossier', 'security');
    window.history.pushState({}, '', u.toString());
    window.dispatchEvent(new PopStateEvent('popstate'));
  } catch {
    /* no-op */
  }
}

const SecurityEvidencePanel: React.FC<{ metrics?: any; selectedAsset?: string }> = ({ metrics, selectedAsset }) => {
  if (!metrics) return null;
  const v2: V2Eval | null = metrics.security_v2 ?? null;
  const comp = (id: string) => v2?.components?.find((c) => c.id === id);
  const incidentVerified = ['CURATED_VERIFIED', 'CURATED_VERIFIED_CLEAN_WINDOW'].includes(metrics.incident_verification);
  const cons = comp('consensus_concentration');
  const disc = comp('disclosure_program');
  const clients = comp('implementation_diversity');
  const bounty = bountyRecordNote(metrics.bug_bounty_evidence);
  const legacy = typeof metrics.security_score === 'number' ? metrics.security_score : null;
  const cov = typeof metrics.security_score_coverage === 'number' ? Math.round(metrics.security_score_coverage * 100) : null;

  const rows: Array<[string, string, string]> = [
    ['Audit evidence', metrics.audit_coverage != null ? `${metrics.audit_coverage} / 100 component` : 'not on file', metrics.security_audit_source === 'github_audit_registry' ? 'VERIFIED' : metrics.audit_coverage != null ? 'CURATED ESTIMATE' : 'NOT ASSESSED'],
    ['Disclosure / bug bounty', bounty ? `${disclosureText(disc, metrics)} · ${bounty.note}` : disclosureText(disc, metrics), bounty?.notScored ? 'NOT SCORED' : bounty?.conflict ? 'NOT VERIFIED' : disc ? (disc.state === 'BINDING' ? 'VERIFIED' : 'NOT SCORED') : 'CURATED'],
    ['Incident basis', incidentVerified ? (metrics.incident_verification === 'CURATED_VERIFIED_CLEAN_WINDOW' ? 'Curated review complete · no scored incident in the review window' : 'Curated review complete · verified incident on record') : 'No verified incident basis', incidentVerified ? 'VERIFIED' : 'NOT VERIFIED'],
    ['Consensus control', cons ? (cons.state === 'BINDING' && cons.score != null ? `Coalition subscore ${cons.score.toFixed(1)} (Security v2 evidence)` : 'Not score-bearing this run') : 'Pending Security v2', cons ? (cons.state === 'BINDING' ? 'VERIFIED' : 'WITHHELD') : 'PENDING'],
    ['Client diversity', clients?.state === 'BINDING' && clients.score != null ? `${clients.score.toFixed(0)}` : 'No sealed source yet', clients?.state === 'BINDING' ? 'VERIFIED' : 'NOT ASSESSED'],
  ];

  return (
    <div className="rounded-md border border-[color:rgb(var(--c-ink-rgb)/0.08)] bg-[color:var(--c-panel-deep)] p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-[13px] font-semibold uppercase tracking-[0.14em] text-[color:var(--c-text)]">Security &amp; resilience · evidence{selectedAsset ? ` · ${selectedAsset.toUpperCase()}` : ''}</h3>
          <Badge state="PARTIAL" />
        </div>
        <button type="button" onClick={openSecurityDossier} className="text-[12px] text-[color:var(--c-gold)] hover:underline focus:outline-none focus-visible:underline">
          Open Security dossier →
        </button>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <dl className="divide-y divide-[color:rgb(var(--c-ink-rgb)/0.05)] text-[13px]">
          {rows.map(([k, v, st]) => (
            <div key={k} className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-3 gap-y-1 py-2 sm:grid-cols-[10rem_minmax(0,1fr)_auto]">
              <dt className="col-span-2 text-[color:var(--c-text-soft)] sm:col-span-1">{k}</dt>
              <dd className="text-[color:var(--c-gray-300)]">{v}</dd>
              <dd><Badge state={st} /></dd>
            </div>
          ))}
        </dl>

        <div className="rounded border border-[color:rgb(var(--c-ink-rgb)/0.07)] bg-[color:var(--c-panel-black)] p-4">
          {/* SR-366 (owner 2026-09-26, option A): a methodology is either score-bearing or diagnostic, never both.
              Until Security v2 binds, legacy v1 IS the current score-bearing methodology (labelled LEGACY · PARTIAL);
              v2 runs in shadow and is not score-bearing. */}
          <div className="text-[11px] uppercase tracking-[0.12em] text-[color:var(--c-text-soft)]">Security score · current methodology</div>
          <div className="mt-1 text-[22px] font-semibold text-[color:var(--c-text)]">{legacy != null ? <>{legacy.toFixed(1)}<span className="ml-1 text-[13px] font-normal text-[color:var(--c-text-soft)]">/100</span></> : '—'}</div>
          <div className="mt-1 font-mono text-[11.5px] text-[color:var(--c-text-strong)]">{(metrics.composite_bridge?.composite_withheld || metrics.composite_withheld) ? 'SECURITY v1 · LEGACY METHODOLOGY · SCORE-BEARING WHEN ELIGIBLE · THIS RUN WITHHELD' : 'SECURITY v1 · LEGACY · PARTIAL · SCORE-BEARING'}{cov != null ? ` · evidence covers ${cov}% of the v1 component weights` : ''}</div>
          <p className="mt-2 text-[12.5px] leading-relaxed text-[color:var(--c-text-body)]">
            Consensus control is not represented in v1; the ratified Security v2 contract replaces it at cutover.
          </p>
          <div className="mt-3 font-mono text-[12px] text-[color:var(--c-text-body)]">
            Security v2 · SHADOW · NOT SCORE-BEARING · {v2 ? (v2.score != null ? `${v2.score.toFixed(1)} · evidence covers ${Math.round(v2.coverage * 100)}% of the v2 component weights (a separate model — coverages are not additive)` : `withheld — ${v2.withheld_reason ?? 'load-bearing evidence missing'}`) : 'not evaluated for this class'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecurityEvidencePanel;
